import { subDays, isSameDay, isSameYear, format, isAfter } from 'date-fns';
import type { OtherUserInfo } from './otherUserInfo';

// Cache for memoizing getDaySeparatorLabel results
// Key is the createdAt timestamp, value is the computed label
const daySeparatorLabelCache = new Map<number, string>();

/**
 * Determines if a timestamp is today
 */
const isTodayDate = (timestamp: Date, today: Date): boolean => isSameDay(timestamp, today);

/**
 * Determines if a timestamp is yesterday
 */
const isYesterdayDate = (timestamp: Date, today: Date): boolean => {
  const yesterday = subDays(today, 1);
  return isSameDay(timestamp, yesterday);
};

/**
 * Determines if a timestamp is within the past week
 */
const isPastWeek = (timestamp: Date, today: Date): boolean => {
  const pastWeek = subDays(today, 7);
  return isSameDay(timestamp, pastWeek) || isAfter(timestamp, pastWeek);
};

/**
 * Format a date for display in the UI based on relative time
 */
const formatDateForDisplay = (
  timestamp: Date,
  today: Date,
  todayLabel: string,
  yesterdayLabel: string,
  weekFormat: string,
  dateFormat: string
): string => {
  if (isTodayDate(timestamp, today)) return todayLabel;
  if (isYesterdayDate(timestamp, today)) return yesterdayLabel;
  if (isPastWeek(timestamp, today)) return format(timestamp, weekFormat);
  return isSameYear(timestamp, today) ? format(timestamp, dateFormat) : format(timestamp, `${dateFormat}, yyyy`);
};

export const getLastMessageDate = (otherUser: OtherUserInfo, lastMessage?: { createdAt?: number }): string => {
  if (otherUser.showDate === false || !lastMessage?.createdAt) return '';

  const timestamp = new Date(lastMessage.createdAt);
  const today = new Date();

  return formatDateForDisplay(timestamp, today, format(timestamp, 'h:mm aa'), 'Yesterday', 'EEEE', 'LLL d');
};

export const formatMessageTime = (createdAt: number): string => format(new Date(createdAt), 'h:mm aa');

export const getDaySeparatorLabel = (createdAt: number): string => {
  // Return cached result if available
  if (daySeparatorLabelCache.has(createdAt)) {
    return daySeparatorLabelCache.get(createdAt)!;
  }

  const timestamp = new Date(createdAt);
  const today = new Date();

  const label = formatDateForDisplay(timestamp, today, 'Today', 'Yesterday', 'EEEE', 'LLL d');

  // Store in cache for future calls
  daySeparatorLabelCache.set(createdAt, label);
  return label;
};

export const getChannelPreviewImage = (otherUser: OtherUserInfo): string =>
  otherUser.image && otherUser.showAvatar ? otherUser.image : '';

export const getChannelPreviewName = (otherUser: OtherUserInfo): string => (otherUser.name ? otherUser.name : 'no name');

export const getLastMessageContent = (otherUser: OtherUserInfo, lastMessage?: { message?: string }): string => {
  if (otherUser.bannerMessage) return otherUser.bannerMessage;
  return lastMessage?.message || '';
};
